import { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import type { DefaultValues, FieldValues, Resolver, UseFormReturn } from 'react-hook-form'
import type { ObjectSchema } from 'yup'
import Button from '@/components/button'
import ConfirmDialog from '@/components/confirm-dialog'
import { useAtualizarCredencialMutation } from '@/http/mutations/credenciais'
import { comCondutaPadrao } from '@/types/credencial'
import type { Credencial } from '@/types/credencial'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import styles from '../style.module.css'

const MENSAGEM_PADRAO =
  'Essa alteração muda como as autorizações desse cliente são processadas por essa credencial, com efeito imediato. Deseja continuar?'

interface CredencialFormProps<T extends FieldValues> {
  credencial: Credencial
  schema: ObjectSchema<T>
  defaultValues: DefaultValues<T>
  montarPayload: (dados: T, credencial: Credencial) => Credencial
  mensagemConfirmacao?: string
  children: (form: UseFormReturn<T>, podeEditar: boolean) => React.ReactNode
}

const CredencialForm = <T extends FieldValues>({
  children,
  credencial,
  defaultValues,
  mensagemConfirmacao = MENSAGEM_PADRAO,
  montarPayload,
  schema,
}: CredencialFormProps<T>) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarCredencial)
  const [dadosPendentes, setDadosPendentes] = useState<T | null>(null)

  const form = useForm<T>({
    resolver: yupResolver(schema) as unknown as Resolver<T>,
    defaultValues,
  })
  const {
    formState: { isDirty },
    handleSubmit,
    reset,
  } = form

  const atualizar = useAtualizarCredencialMutation(() => {
    if (dadosPendentes) reset(dadosPendentes)
    setDadosPendentes(null)
  })

  const onSubmit = (dados: T) => setDadosPendentes(dados)

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        {children(form, podeEditar)}

        {podeEditar ? (
          <div className={styles.actions}>
            <Button type="submit" disabled={!isDirty}>
              Salvar alterações
            </Button>
          </div>
        ) : null}
      </form>

      <ConfirmDialog
        isOpen={Boolean(dadosPendentes)}
        toggle={() => setDadosPendentes(null)}
        title="Confirmar alteração da credencial"
        message={mensagemConfirmacao}
        confirmLabel="Salvar alterações"
        tone="warning"
        isLoading={atualizar.isPending}
        onConfirm={() => {
          if (!dadosPendentes) return
          atualizar.mutate(comCondutaPadrao(montarPayload(dadosPendentes, credencial)))
        }}
      />
    </>
  )
}

export default CredencialForm
